/**
 * @class ReadMore
 * @extends HTMLElement
 * @description Custom element pour tronquer le contenu avec toggle "Voir plus/Replier"
 */
class ReadMore extends HTMLElement {
  constructor() {
    super();
    
    /** @type {HTMLElement|null} */
    this.toggleButton = this.nextElementSibling;
    
    /** @type {string} */
    this.initContentHeight = '';
    
    /** @type {number} */
    this.contentHeight = 0;
    
    /** @type {number|null} */
    this.resizeTimeout = null;
  }

  connectedCallback() {
    if (!this.toggleButton || !this.toggleButton.classList.contains('read-more__toggle')) {
      return;
    }

    try {
      const computedStyle = window.getComputedStyle(this);
      this.initContentHeight = computedStyle.getPropertyValue('--read-more-max-height').trim();
      
      this.checkOverflow();
      
      this.toggleButton.addEventListener('click', this.toggle.bind(this));
      this.toggleButton.addEventListener('keydown', this.handleKeydown.bind(this));
      
      this.handleResize = this.debounce(this.checkOverflow.bind(this), 250);
      window.addEventListener('resize', this.handleResize);
    } catch (error) {
      console.error('ReadMore initialization error:', error);
    }
  }

  disconnectedCallback() {
    if (this.handleResize) {
      window.removeEventListener('resize', this.handleResize);
    }
  }

  /**
   * @description Vérifie si le contenu dépasse la hauteur maximale et affiche/masque le bouton
   * @returns {void}
   */
  checkOverflow() {
    const content = this.querySelector('.read-more__content');
    if (!content) {
      return;
    }

    const isOverflowing = content.scrollHeight > this.clientHeight;
    
    // visibility et non display : la place du bouton est réservée en CSS dès le
    // premier paint (mobile), sinon sa révélation après mesure ajoute 32px et
    // décale toute la zone conseil. Desktop reste masqué par display:none !important.
    this.toggleButton.style.visibility = isOverflowing ? 'visible' : 'hidden';
  }

  /**
   * @description Toggle l'état d'expansion du contenu
   * @returns {void}
   */
  toggle() {
    const isExpanded = this.dataset.expanded === 'true';
    this.setAttribute('data-expanded', isExpanded ? 'false' : 'true');
    
    const content = this.querySelector('.read-more__content');
    if (!content) {
      return;
    }

    this.contentHeight = content.getBoundingClientRect().height;
    this.style.setProperty(
      '--read-more-max-height',
      isExpanded ? this.initContentHeight : `${this.contentHeight}px`
    );
  }

  /**
   * @description Gestion de la navigation clavier
   * @param {KeyboardEvent} event
   * @returns {void}
   */
  handleKeydown(event) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.toggle();
    }
  }

  /**
   * @description Debounce une fonction pour limiter sa fréquence d'exécution
   * @param {Function} func - Fonction à debouncer
   * @param {number} wait - Délai d'attente en millisecondes
   * @returns {Function}
   */
  debounce(func, wait) {
    return (...args) => {
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = setTimeout(() => func.apply(this, args), wait);
    };
  }
}

customElements.define('read-more', ReadMore);